import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { router } from '@/to-fsd/app';
import { ROUTE_NAMES } from '@/to-fsd/shared/constants';
import { AtToastPlugin } from '@packages/aliftech-ui';
import { i18n } from '@/to-fsd/shared/lib/i18n';
import { useUserStore } from '@/to-fsd/shared/store/user';
import { useNasiyaGlobalStore } from '@/to-fsd/shared/store/nasiya-global';
import { useMainStore } from '@/to-fsd/shared/store/main';
import { refreshToken as apiRefreshToken } from '@/to-fsd/shared/lib/keycloak/api/endpoints';

const pandaCache = new InMemoryCache();

const link = createHttpLink({
  uri: process.env.VUE_APP_PANDA_GRAPHQL,
  fetch: async (uri, options) => {
    const refreshToken = localStorage.getItem('refresh_token');

    if (refreshToken) {
      try {
        const res = await apiRefreshToken({ refreshToken });

        const user = useUserStore();

        await user.fetchUser();

        localStorage.setItem('access_token', res.data.access_token);
        localStorage.setItem('refresh_token', res.data.refresh_token);

        const now = Math.floor(Date.now() / 1000);
        const accessTokenExpiresIn = now + res.data.expires_in - 5;
        const refreshTokenExpiresIn = now + res.data.refresh_expires_in - 5;

        localStorage.setItem('access_token_expire', accessTokenExpiresIn);
        localStorage.setItem('refresh_token_expire', refreshTokenExpiresIn);
      } catch {
        const user = useUserStore();
        const globalStore = useNasiyaGlobalStore();
        const mainStore = useMainStore();

        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token_expire');
        localStorage.removeItem('refresh_token_expire');

        user.$reset();
        globalStore.$reset();
        mainStore.$reset();

        router.replace({ name: ROUTE_NAMES.login });

        AtToastPlugin.error({
          title: '[KC]' + i18n.global.t('app.loginAgainRequired'),
          duration: 4000,
        });
      }
    }

    options.headers = getHeaders();

    return fetch(uri, options);
  },
});

const errorLink = onError(err => {
  const user = useUserStore();
  const globalStore = useNasiyaGlobalStore();
  const mainStore = useMainStore();

  user.$reset();
  globalStore.$reset();
  mainStore.$reset();

  router.replace({ name: ROUTE_NAMES.login });
  for (const gqlError of err.graphQLErrors) {
    AtToastPlugin.error({
      title: gqlError?.message || i18n.global.t('app.unknownError'),
      duration: 4000,
    });
  }
});

export const pandaClient = new ApolloClient({
  link: errorLink.concat(link),
  cache: pandaCache,
});

function getHeaders() {
  const headers = {};
  const token = localStorage.getItem('access_token');
  if (token) {
    headers['Authorization'] = 'Bearer ' + token;
  }
  headers['Content-Type'] = 'application/json';
  return headers;
}

pandaClient.defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
  },
  query: {
    fetchPolicy: 'no-cache',
  },
};
